import {
  SEPARATION_ANSWER_VALUES,
  SeparationAnswerValue,
} from '@/features/onboarding/separation/separation.constants'
import type {
  SeparationAnswerPayload,
  SeparationQuestion,
} from '@/features/onboarding/separation/separation.types'
import { transactionIdsFor } from '@/features/onboarding/separation/transactionIds'

export interface QuestionAnswer {
  selectedOption: string | null
  isExpanded: boolean
  subItemSelections: Record<string, string>
  note: string
}

const isAnswerValue = (value: string): value is SeparationAnswerValue =>
  SEPARATION_ANSWER_VALUES.includes(value)

/** An unanswered question starts on the suggestion, every row with it, so confirming is one click. */
export const suggestedAnswer = (
  question: SeparationQuestion
): QuestionAnswer => {
  const suggested = question.toggleOptions.find(
    (option) =>
      option.value === question.suggested &&
      option.value !== question.expandValue
  )?.value

  return {
    selectedOption: suggested ?? null,
    isExpanded: false,
    subItemSelections: suggested
      ? Object.fromEntries(
          question.subItems.map((subItem) => [subItem.id, suggested])
        )
      : {},
    note: '',
  }
}

/**
 * Turns one question's selection into per-transaction answers. Sub-item ids are transaction
 * ids, so a group-level choice fans out across the group and "It's a Mix" sends each row's
 * own value. The group's one note goes with whatever it marks unsure.
 */
export const answersForQuestion = (
  question: SeparationQuestion,
  selectedOption: string,
  subItemSelections: Record<string, string>,
  note: string
): SeparationAnswerPayload[] => {
  const ids = question.subItems.flatMap(transactionIdsFor)
  if (ids.length === 0) return []

  const trimmedNote = note.trim()
  const withNote = (
    value: SeparationAnswerValue,
    transactionIds: number[]
  ): SeparationAnswerPayload =>
    value === SeparationAnswerValue.Unsure && trimmedNote
      ? { transactionIds, value, note: trimmedNote }
      : { transactionIds, value }

  if (isAnswerValue(selectedOption)) {
    return [withNote(selectedOption, ids)]
  }

  const byValue = new Map<SeparationAnswerValue, number[]>()
  for (const subItem of question.subItems) {
    const choice = subItemSelections[subItem.id]
    if (!choice || !isAnswerValue(choice)) continue
    byValue.set(choice, [
      ...(byValue.get(choice) ?? []),
      ...transactionIdsFor(subItem),
    ])
  }

  return [...byValue.entries()].map(([value, transactionIds]) =>
    withNote(value, transactionIds)
  )
}

/**
 * The top-level choice a fully answered row set implies. An all-`unsure` set falls back to
 * the expand sentinel unless the question offers "I'm not sure" at the top level.
 */
export const unanimousTopLevelOption = (
  question: SeparationQuestion,
  selections: Record<string, string>
): string => {
  const values = new Set(
    question.subItems.map((subItem) => selections[subItem.id])
  )
  if (values.size !== 1) return question.expandValue
  const [only] = [...values]
  if (only === question.unsureValue) return only
  return question.toggleOptions.some((option) => option.value === only)
    ? only
    : question.expandValue
}

export const hasUnsureRows = (
  question: SeparationQuestion,
  selections: Record<string, string>
): boolean =>
  question.subItems.some(
    (subItem) => selections[subItem.id] === SeparationAnswerValue.Unsure
  )

export const unsureTransactionCount = (
  question: SeparationQuestion,
  selections: Record<string, string>
): number =>
  question.subItems
    .filter(
      (subItem) => selections[subItem.id] === SeparationAnswerValue.Unsure
    )
    .flatMap(transactionIdsFor).length

/** Unsure rows need a note, since that is what the bookkeeper acts on. */
export const hasUnresolvedUnsureNote = (
  question: SeparationQuestion,
  selections: Record<string, string>,
  note: string
): boolean => hasUnsureRows(question, selections) && !note.trim()
